import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

function Documentation() {
  const [activeSection, setActiveSection] = useState('getting-started');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['getting-started', 'usage', 'data-management', 'security', 'troubleshooting'];
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-background-light dark:bg-background-dark text-[#111318] dark:text-white">
      <Header />
      <div className="max-w-[1200px] mx-auto flex flex-col md:flex-row min-h-[calc(100vh-64px)]">
        {/* Side Navigation Bar */}
        <aside className="hidden md:block w-full md:w-64 flex-shrink-0 border-r border-[#f0f2f4] dark:border-gray-800 p-6 md:sticky md:top-16 h-auto md:h-[calc(100vh-64px)] overflow-y-auto">
          <div className="flex flex-col gap-6">
            <div>
              <h1 className="text-[#111318] dark:text-white text-sm font-bold uppercase tracking-wider mb-1">التوثيق</h1>
              <p className="text-[#616f89] text-xs font-medium">الإصدار 1.2.0 (مستقر)</p>
            </div>
            <nav className="flex flex-col gap-1">
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('getting-started');
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  activeSection === 'getting-started'
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-[#616f89] dark:text-gray-400 hover:bg-[#f0f2f4] dark:hover:bg-gray-800'
                }`}
                href="#getting-started"
              >
                <span className="material-symbols-outlined text-lg">rocket_launch</span>
                <span className="text-base">البدء السريع</span>
              </a>
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('usage');
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  activeSection === 'usage'
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-[#616f89] dark:text-gray-400 hover:bg-[#f0f2f4] dark:hover:bg-gray-800'
                }`}
                href="#usage"
              >
                <span className="material-symbols-outlined text-lg">ads_click</span>
                <span className="text-base font-medium">الاستخدام</span>
              </a>
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('data-management');
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  activeSection === 'data-management'
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-[#616f89] dark:text-gray-400 hover:bg-[#f0f2f4] dark:hover:bg-gray-800'
                }`}
                href="#data-management"
              >
                <span className="material-symbols-outlined text-lg">database</span>
                <span className="text-base font-medium">إدارة البيانات</span>
              </a>
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('security');
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  activeSection === 'security'
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-[#616f89] dark:text-gray-400 hover:bg-[#f0f2f4] dark:hover:bg-gray-800'
                }`}
                href="#security"
              >
                <span className="material-symbols-outlined text-lg">shield_lock</span>
                <span className="text-base font-medium">الأمان</span>
              </a>
              <a 
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('troubleshooting');
                }}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  activeSection === 'troubleshooting'
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-[#616f89] dark:text-gray-400 hover:bg-[#f0f2f4] dark:hover:bg-gray-800'
                }`}
                href="#troubleshooting"
              >
                <span className="material-symbols-outlined text-lg">help</span>
                <span className="text-base font-medium">حل المشاكل</span>
              </a>
            </nav>
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
              <p className="text-[#111318] dark:text-white text-xs font-bold mb-3">الموارد</p>
              <div className="flex flex-col gap-3">
                <a className="text-xs text-[#616f89] dark:text-gray-400 hover:text-primary" href="#">مرجع API</a>
                <a className="text-xs text-[#616f89] dark:text-gray-400 hover:text-primary" href="#">منتدى المجتمع</a>
                <a className="text-xs text-[#616f89] dark:text-gray-400 hover:text-primary" href="#">ملاحظات الإصدار</a>
              </div>
            </div>
          </div>
        </aside>
        {/* Main Content Area */}
        <main className="flex-1 px-6 md:px-12 pt-4 md:pt-8 pb-8 max-w-[800px]">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 mb-8 text-sm font-medium text-[#616f89] dark:text-gray-400">
            <a className="hover:text-primary" href="#">التوثيق</a>
            <span className="material-symbols-outlined text-base">chevron_right</span>
            <span className="text-[#111318] dark:text-white">البدء السريع</span>
          </nav>
          {/* Page Heading */}
          <div className="mb-12">
            <h1 className="text-[#111318] dark:text-white text-3xl md:text-5xl font-black leading-tight tracking-tight mb-4 whitespace-nowrap">
              البدء مع Formly AI
            </h1>
            <p className="text-[#616f89] dark:text-gray-400 text-lg leading-relaxed">
              مرحباً بك في توثيق Formly AI. تعلم كيفية التثبيت والتكوين وتعظيم كفاءتك مع مساعد ملء النماذج الأذكى في العالم.
            </p>
            <div className="mt-6 flex gap-2 md:gap-3">
              <Link to="/install" className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-bold shadow-md whitespace-nowrap flex-1 md:flex-none">
                <span className="material-symbols-outlined text-lg">download</span>
                تثبيت لـ Chrome
              </Link>
              <button className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15 hover:border-primary/40 transition-colors flex-1 md:flex-none">
                <span className="material-symbols-outlined text-lg">video_library</span>
                شاهد عرض 2 دقيقة
              </button>
            </div>
          </div>
          {/* Content Sections */}
          <section className="space-y-12">
            {/* Installation */}
            <div id="getting-started">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <span className="material-symbols-outlined text-primary">install_desktop</span>
                </div>
                <h2 className="text-2xl font-bold text-[#111318] dark:text-white">التثبيت السريع</h2>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-6 leading-relaxed">
                Formly AI متاح كإضافة Chrome. اتبع هذه الخطوات الثلاث البسيطة للبدء في أقل من 60 ثانية.
              </p>
              <div className="space-y-4">
                <div className="group flex gap-4 p-5 md:p-6 bg-gradient-to-r from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/15 dark:border-primary/30 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold group-hover:bg-primary group-hover:text-white transition-all">
                    1
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-base md:text-lg text-[#111318] dark:text-white">زيارة متجر الويب</h3>
                    <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">
                      انتقل إلى <a className="text-primary underline decoration-primary/30" href="#">Chrome Web Store</a> وابحث عن "Formly AI".
                    </p>
                  </div>
                </div>
                <div className="group flex gap-4 p-5 md:p-6 bg-gradient-to-r from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/15 dark:border-primary/30 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold group-hover:bg-primary group-hover:text-white transition-all">
                    2
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-base md:text-lg text-[#111318] dark:text-white">إضافة إلى Chrome</h3>
                    <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">
                      انقر على زر "إضافة إلى Chrome" وأكد حوار الأذونات.
                    </p>
                  </div>
                </div>
                <div className="group flex gap-4 p-5 md:p-6 bg-gradient-to-r from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/15 dark:border-primary/30 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="flex-shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold group-hover:bg-primary group-hover:text-white transition-all">
                    3
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-base md:text-lg text-[#111318] dark:text-white">تثبيت الإضافة</h3>
                    <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">
                      للوصول السهل، انقر على أيقونة اللغز في Chrome وثبت Formly AI في شريط الأدوات الخاص بك.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Usage Section */}
            <div id="usage">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <span className="material-symbols-outlined text-primary">ads_click</span>
                </div>
                <h2 className="text-2xl font-bold text-[#111318] dark:text-white">الاستخدام</h2>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-6 leading-relaxed">
                تعلم كيفية استخدام Formly AI بفعالية لملء النماذج بسرعة ودقة.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">play_circle</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">كيفية تفعيل الملء التلقائي</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">بعد تثبيت الإضافة، انتقل إلى أي نموذج واضغط على أيقونة Formly في شريط الأدوات أو استخدم اختصار لوحة المفاتيح Alt+F.</p>
                </div>
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">person</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">تخصيص الملف الشخصي</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">يمكنك إنشاء ملفات شخصية متعددة لحالات استخدام مختلفة. استخدم لوحة التحكم لإدارة ملفاتك الشخصية.</p>
                </div>
              </div>
            </div>
            {/* Usage Shortcuts */}
            <div>
              <div className="flex items-center justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary">keyboard</span>
                  </div>
                  <h2 className="text-2xl font-bold text-[#111318] dark:text-white">
                    اختصارات لوحة المفاتيح
                  </h2>
                </div>
                <span className="hidden md:inline-flex px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  للوصول الأسرع إلى Formly AI
                </span>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-4 text-sm md:text-base">
                استخدم هذه الاختصارات للتحكم في Formly AI بدون مغادرة لوحة المفاتيح.
              </p>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-lg p-4 md:p-6 font-mono text-sm">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all">
                    <span className="text-[#111318] dark:text-white text-sm md:text-base">
                      تفعيل الملء التلقائي
                    </span>
                    <div className="flex gap-1">
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        Alt
                      </kbd>
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        F
                      </kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all">
                    <span className="text-[#111318] dark:text-white text-sm md:text-base">
                      تبديل الملف الشخصي
                    </span>
                    <div className="flex gap-1">
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        Alt
                      </kbd>
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        P
                      </kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all">
                    <span className="text-[#111318] dark:text-white text-sm md:text-base">
                      فتح لوحة التحكم
                    </span>
                    <div className="flex gap-1">
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        Alt
                      </kbd>
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        D
                      </kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 hover:border-primary/50 hover:bg-primary/5 dark:hover:bg-primary/10 transition-all">
                    <span className="text-[#111318] dark:text-white text-sm md:text-base">
                      مسح النموذج
                    </span>
                    <div className="flex gap-1">
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        Alt
                      </kbd>
                      <kbd className="px-2.5 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-sm text-xs md:text-sm">
                        Esc
                      </kbd>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Data Management Section */}
            <div id="data-management">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <span className="material-symbols-outlined text-primary">database</span>
                </div>
                <h2 className="text-2xl font-bold text-[#111318] dark:text-white">إدارة البيانات</h2>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-6 leading-relaxed">
                تعلم كيفية إدارة ملفاتك الشخصية والبيانات المخزنة في Formly AI.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">add_circle</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">إنشاء ملف شخصي جديد</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">افتح لوحة التحكم وانتقل إلى قسم الملفات الشخصية. انقر على "إضافة ملف شخصي جديد" واملأ المعلومات المطلوبة.</p>
                </div>
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">import_export</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">تصدير واستيراد البيانات</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">يمكنك تصدير ملفاتك الشخصية كملف JSON واستيرادها على أجهزة أخرى. البيانات مشفرة دائماً.</p>
                </div>
              </div>
            </div>
            {/* Security Section */}
            <div id="security">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <span className="material-symbols-outlined text-primary">shield_lock</span>
                </div>
                <h2 className="text-2xl font-bold text-[#111318] dark:text-white">الأمان</h2>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-6 leading-relaxed">
                تعرف على كيفية حماية Formly AI لبياناتك الشخصية.
              </p>
            </div>
            {/* Important Notice */}
            <div className="p-6 bg-blue-50 dark:bg-primary/5 border-l-4 border-primary rounded-r-xl">
              <div className="flex items-start gap-4">
                <span className="material-symbols-outlined text-primary">info</span>
                <div>
                  <h4 className="font-bold text-primary mb-1">الأمان أولاً</h4>
                  <p className="text-base text-blue-900 dark:text-blue-200 leading-relaxed">
                    Formly AI يخزن بيانات ملفك الشخصي الحساسة محلياً على جهازك. نستخدم تشفير AES-256 لجميع الحقول المخزنة. لا يتم رفع بياناتك إلى خوادمنا أبداً إلا إذا قمت بتمكين المزامنة السحابية صراحة.
                  </p>
                </div>
              </div>
            </div>
            {/* Troubleshooting Section */}
            <div id="troubleshooting">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <span className="material-symbols-outlined text-primary">help</span>
                </div>
                <h2 className="text-2xl font-bold text-[#111318] dark:text-white">حل المشاكل</h2>
              </div>
              <p className="text-[#616f89] dark:text-gray-400 mb-6 leading-relaxed">
                حلول للمشاكل الشائعة التي قد تواجهها عند استخدام Formly AI.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">bug_report</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">الإضافة لا تعمل على موقع معين</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">تأكد من أن الإضافة مفعلة وأن الموقع ليس في قائمة الاستثناءات. جرب تحديث الصفحة أو إعادة تحميل الإضافة.</p>
                </div>
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">sync_problem</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">البيانات لا تُملأ بشكل صحيح</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">تحقق من أن ملفك الشخصي محدث وأن الحقول في النموذج واضحة. يمكنك استخدام وضع التصحيح لمعرفة ما يحدث.</p>
                </div>
                <div className="group p-6 bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10 border border-primary/20 dark:border-primary/30 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 md:col-span-2">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 dark:bg-primary/20 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                      <span className="material-symbols-outlined text-primary group-hover:text-white">lock_reset</span>
                    </div>
                    <h3 className="font-bold text-lg text-[#111318] dark:text-white">نسيت كلمة المرور</h3>
                  </div>
                  <p className="text-base text-[#616f89] dark:text-gray-400 leading-relaxed">Formly AI لا يستخدم كلمات مرور. جميع البيانات محلية على جهازك. إذا فقدت الوصول، يمكنك استيراد نسخة احتياطية.</p>
                </div>
              </div>
            </div>
            {/* illustrative Card */}
            <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary to-blue-400 group">
              <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:bg-transparent"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur-sm p-6 rounded-xl shadow-lg w-3/4 max-w-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center">
                      <span className="material-symbols-outlined">person</span>
                    </div>
                    <div>
                      <div className="h-3 w-24 bg-gray-200 rounded mb-1"></div>
                      <div className="h-2 w-16 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="h-8 bg-primary/10 rounded border-dashed border border-primary/40 flex items-center px-3">
                      <span className="text-[10px] font-mono text-primary">Filling form fields... 85%</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="h-2 bg-gray-100 rounded"></div>
                      <div className="h-2 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="absolute bottom-4 left-4 text-white/80 text-xs font-medium bg-black/20 px-2 py-1 rounded backdrop-blur-sm">تمثيل مرئي لمنطق ربط الذكاء الاصطناعي</p>
            </div>
            {/* Feedback Widget */}
            <div className="pt-12 mt-12 border-t border-gray-100 dark:border-gray-800">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
                <div>
                  <h3 className="text-lg font-bold mb-1">هل كانت هذه المقالة مفيدة؟</h3>
                  <p className="text-base text-[#616f89] dark:text-gray-400">ساعدنا في تحسين توثيق Formly AI.</p>
                </div>
                <div className="flex gap-4">
                  <button className="flex items-center gap-2 px-6 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap">
                    <span className="material-symbols-outlined text-green-500">thumb_up</span>
                    نعم
                  </button>
                  <button className="flex items-center gap-2 px-6 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap">
                    <span className="material-symbols-outlined text-red-500">thumb_down</span>
                    لا
                  </button>
                </div>
              </div>
            </div>
            {/* Pagination/Nav */}
            <div className="flex items-center justify-center py-8">
              <a className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg text-sm font-bold hover:opacity-90 transition-opacity shadow-md" href="#">
                <span className="material-symbols-outlined">arrow_forward</span>
                <span>الاستخدام المتقدم</span>
              </a>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default Documentation;

